import {ChangeDetectorRef, Component, inject, OnDestroy} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {CertificateTemplateItem, CertificateTemplateService} from '../../../core/services/certificateTemplate.service';
import {Router} from '@angular/router';
import {Font, Template} from '@pdfme/common';
import {generate} from '@pdfme/generator';
import {pdf2img} from '@pdfme/converter';
import {text, image, line} from '@pdfme/schemas';
import {firstValueFrom, tap} from 'rxjs';

@Component({
  selector: 'app-certificate',
  templateUrl: './certificate.component.html',
  styleUrls: ['./certificate.component.scss'],
  standalone: false
})
export class CertificateComponent implements OnDestroy {
  private certificateTemplateService = inject(CertificateTemplateService);
  private router = inject(Router);
  private changeDetector = inject(ChangeDetectorRef);
  private previewUrls = new Map<number, string>();
  private destroyed = false;
  private previewGeneration = 0;
  private certificateFontsPromise?: Promise<Font>;

  public templates = toSignal(this.certificateTemplateService.templates().pipe(
    tap(templates => void this.generatePreviews(templates)),
  ), {initialValue: []});

  getPreviewUrl(templateId: number): string | undefined {
    return this.previewUrls.get(templateId);
  }

  private async generatePreviews(templates: CertificateTemplateItem[]): Promise<void> {
    const generation = ++this.previewGeneration;
    this.clearPreviewUrls();

    await Promise.all(templates.map(async templateItem => {
      try {
        const template = structuredClone(templateItem.template);
        await this.loadTemplateImages(template);
        if (this.destroyed || generation !== this.previewGeneration) {
          return;
        }

        const previewUrl = await this.createTemplatePreview(template);
        if (this.destroyed || generation !== this.previewGeneration) {
          URL.revokeObjectURL(previewUrl);
          return;
        }

        this.previewUrls.set(templateItem.id, previewUrl);
        this.changeDetector.markForCheck();
      } catch (error) {
        console.error(
          `Failed to generate certificate preview for template ${templateItem.id}`,
          error,
        );
      }
    }));
  }

  private async loadTemplateImages(template: Template): Promise<void> {
    await Promise.all(template.schemas.flat().map(async schema => {
      const assetId = (schema as {assetId?: string | number}).assetId;
      if (schema.type !== 'image' || !assetId) {
        return;
      }

      const url = await firstValueFrom(this.certificateTemplateService.getImage(Number(assetId)));
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to load certificate image: ${response.status}`);
      }

      const blob = await response.blob();
      schema.content = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          if (typeof reader.result === 'string') {
            resolve(reader.result);
          } else {
            reject(new Error('Failed to read certificate image'));
          }
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
      });
    }));
  }

  private async createTemplatePreview(template: Template): Promise<string> {
    const inputs: Record<string, string> = {};
    for (const page of template.schemas) {
      for (const schema of page) {
        inputs[schema.name] = schema.content ?? '';
      }
    }

    const font = await this.loadCertificateFonts();
    const pdf = await generate({
      template,
      inputs: [inputs],
      plugins: {Text: text, Image: image, Line: line},
      options: {font},
    });
    const pdfBuffer = pdf.buffer.slice(
      pdf.byteOffset,
      pdf.byteOffset + pdf.byteLength,
    ) as ArrayBuffer;

    const [firstPage] = await pdf2img(pdfBuffer, {
      range: { start: 0, end: 1 },
    });
    if (!firstPage) {
      throw new Error('Certificate preview has no pages');
    }

    return URL.createObjectURL(new Blob([firstPage], {type: 'image/png'}));
  }

  private clearPreviewUrls(): void {
    for (const url of this.previewUrls.values()) {
      URL.revokeObjectURL(url);
    }
    this.previewUrls.clear();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.clearPreviewUrls();
  }

  openTemplate(templateId: number) {
    this.router.navigate(['/pages/certificate/detail'], {queryParams: {id: templateId}});
    return;
  }

  private loadCertificateFonts(): Promise<Font> {
    if (!this.certificateFontsPromise) {
      this.certificateFontsPromise = this.fetchCertificateFonts();
    }

    return this.certificateFontsPromise;
  }

  private async fetchCertificateFonts(): Promise<Font> {
    const [
      roboto,
      robotoBold,
      robotoItalic,

      montserrat,
      montserratBold,

      playfair,
      playfairItalic,
      playfairBold,

      calibri,
      calibriBold,
    ] = await Promise.all([
      this.loadFont('/assets/fonts/Roboto-Regular.ttf'),
      this.loadFont('/assets/fonts/Roboto-Bold.ttf'),
      this.loadFont('/assets/fonts/Roboto-Italic.ttf'),

      this.loadFont('/assets/fonts/Montserrat-Regular.ttf'),
      this.loadFont('/assets/fonts/Montserrat-Bold.ttf'),

      this.loadFont('/assets/fonts/PlayfairDisplay-Regular.ttf'),
      this.loadFont('/assets/fonts/PlayfairDisplay-Italic.ttf'),
      this.loadFont('/assets/fonts/PlayfairDisplay-Bold.ttf'),
      this.loadFont('/assets/fonts/calibri-regular.ttf'),
      this.loadFont('/assets/fonts/calibri-bold.ttf'),
    ]);

    return {
      Roboto: {
        data: roboto,
        fallback: true,
      },

      'Roboto Bold': {
        data: robotoBold,
      },

      'Roboto Italic': {
        data: robotoItalic,
      },

      Montserrat: {
        data: montserrat,
      },

      'Montserrat Bold': {
        data: montserratBold,
      },

      'Playfair Display': {
        data: playfair,
      },

      'Playfair Display Italic': {
        data: playfairItalic,
      },

      'Playfair Display Bold': {
        data: playfairBold,
      },

      'Calibri': {
        data: calibri,
      },

      'Calibri Bold': {
        data: calibriBold,
      }
    };
  }

  private async loadFont(url: string): Promise<ArrayBuffer> {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(
        `Failed to load font ${url}: ${response.status}`,
      );
    }

    return response.arrayBuffer();
  }
}
